import {useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type MouseEvent} from 'react'
import {Carousel} from 'react-bootstrap'

const SENJA_WIDGET_ID = 'de0b05db-cb29-493c-9195-1450c09d8080'
const SENJA_WIDGET_CONFIG_URL = `https://widget.senja.io/api/widget-config/${SENJA_WIDGET_ID}`

type SenjaReview = {
  id: string
  text: string
  html: string
  date: string
  rating: number
  title?: string
  url?: string
  integration?: string
  customer: {
    name: string
    avatar: string
    company?: string | null
    position?: string | null
    tagline?: string | null
  }
}

type SenjaWidgetConfigResponse = {
  reviews: SenjaReview[]
  widget: {
    filters?: {
      review_ids?: string[]
    }
  }
}

const formatReviewDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

const orderReviews = (reviews: SenjaReview[], reviewIds?: string[]) => {
  if (!reviewIds?.length) return reviews

  const reviewsById = new Map(reviews.map((review) => [review.id, review]))
  return reviewIds.map((id) => reviewsById.get(id)).filter((review): review is SenjaReview => !!review)
}

const getIntegrationBadgeLabel = (integration?: string) => {
  if (integration === 'shopify') return 'Shopify App Store'
  if (integration === 'g2') return 'G2'
  return null
}

const getReviewSourceLabel = (integration?: string) => {
  if (integration === 'shopify') return 'View on Shopify App Store'
  if (integration === 'g2') return 'View on G2'
  return 'View original review'
}

const getCustomerSubtitle = (customer: SenjaReview['customer']) => {
  const parts = [customer.position, customer.company?.trim(), customer.tagline?.trim()].filter(Boolean)
  return parts.join(' · ')
}

const REVIEW_CARD_STYLE: CSSProperties = {
  color: '#1f2937',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.55)',
}

const openReview = (url?: string) => {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

const ReviewAvatar = ({name, avatar}: {name: string; avatar: string}) => {
  const [imageFailed, setImageFailed] = useState(false)
  const initials = getInitials(name)

  if (!avatar || imageFailed) {
    return (
      <div
        className='h-40px w-40px rounded-circle bg-light text-secondary fs-6 fw-bold d-flex align-items-center justify-content-center flex-shrink-0'
        aria-hidden='true'
      >
        {initials || '?'}
      </div>
    )
  }

  return (
    <div className='rounded-circle overflow-hidden flex-shrink-0' style={{width: '40px', height: '40px'}}>
      <img
        src={avatar}
        alt={`${name} avatar`}
        style={{width: '100%', height: '100%', objectFit: 'cover'}}
        onError={() => setImageFailed(true)}
      />
    </div>
  )
}

const SenjaReviewsCarousel = () => {
  const [reviews, setReviews] = useState<SenjaReview[]>([])
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    let cancelled = false

    fetch(SENJA_WIDGET_CONFIG_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Senja widget config failed (${response.status})`)
        }
        return response.json() as Promise<SenjaWidgetConfigResponse>
      })
      .then((data) => {
        if (cancelled) return
        setReviews(orderReviews(data.reviews ?? [], data.widget?.filters?.review_ids))
      })
      .catch((error) => {
        console.warn('Senja reviews failed to load', error)
        if (!cancelled) setReviews([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const hasReviews = reviews.length > 0
  const carouselKey = useMemo(() => reviews.map((review) => review.id).join(','), [reviews])
  const showControls = reviews.length > 1

  useEffect(() => {
    setActiveIndex(0)
  }, [carouselKey])

  const goToPrevious = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setActiveIndex((current) => (current === 0 ? reviews.length - 1 : current - 1))
  }

  const goToNext = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setActiveIndex((current) => (current === reviews.length - 1 ? 0 : current + 1))
  }

  const handleReviewCardClick = (url?: string) => {
    openReview(url)
  }

  const handleReviewCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, url?: string) => {
    if (!url) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openReview(url)
    }
  }

  if (loading) {
    return (
      <div
        className='w-100 mx-auto p-4 py-10 rounded d-flex align-items-center justify-content-center'
        style={REVIEW_CARD_STYLE}
      >
        <span className='text-muted fs-7'>Loading reviews...</span>
      </div>
    )
  }

  if (!hasReviews) return null

  return (
    <div className='w-100 mx-auto' style={{maxWidth: '620px'}}>
      <div className='position-relative mx-auto w-100'>
        {showControls && (
          <div
            className='position-absolute d-flex align-items-center gap-1'
            style={{top: '1rem', right: '1rem', zIndex: 2}}
          >
            <button
              type='button'
              className='btn btn-sm border-0 bg-transparent p-0 lh-1 text-gray-600 opacity-75'
              onClick={goToPrevious}
              aria-label='Previous review'
            >
              <i className='bi bi-chevron-left fs-4' />
            </button>
            <button
              type='button'
              className='btn btn-sm border-0 bg-transparent p-0 lh-1 text-gray-600 opacity-75'
              onClick={goToNext}
              aria-label='Next review'
            >
              <i className='bi bi-chevron-right fs-4' />
            </button>
          </div>
        )}

        <Carousel
          key={carouselKey}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
          color='success'
          controls={false}
          indicators={false}
          fade
          className='w-100'
        >
          {reviews.map((review) => {
            const integrationBadge = getIntegrationBadgeLabel(review.integration)
            const customerSubtitle = getCustomerSubtitle(review.customer)
            const isClickable = Boolean(review.url)

            return (
              <Carousel.Item key={review.id}>
                <div
                  className={`w-100 p-4 rounded d-flex flex-column${
                    isClickable ? ' cursor-pointer' : ''
                  }`}
                  style={REVIEW_CARD_STYLE}
                  onClick={() => handleReviewCardClick(review.url)}
                  onKeyDown={(event) => handleReviewCardKeyDown(event, review.url)}
                  role={isClickable ? 'link' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-label={isClickable ? `Open ${review.customer.name}'s review` : undefined}
                >
                  <div className='d-flex align-items-center gap-2 mb-2 pe-10 flex-shrink-0'>
                    <ReviewAvatar name={review.customer.name} avatar={review.customer.avatar} />
                    <div className='min-w-0'>
                      <p className='mb-0 lh-sm fw-bold text-truncate'>{review.customer.name}</p>
                      {customerSubtitle && (
                        <small className='text-muted lh-sm d-block text-truncate'>{customerSubtitle}</small>
                      )}
                    </div>
                  </div>

                  {integrationBadge && (
                    <span className='badge badge-light text-dark fw-semibold mb-2 align-self-start flex-shrink-0'>
                      {integrationBadge}
                    </span>
                  )}

                  {review.title?.trim() && (
                    <p className='fw-bold fs-7 mb-2 flex-shrink-0 lh-sm'>{review.title.trim()}</p>
                  )}

                  <div className='d-flex align-items-center justify-content-between mb-2 flex-shrink-0'>
                    <img
                      src={`https://cdn.trustpilot.net/brand-assets/4.1.0/stars/stars-${Math.min(
                        5,
                        Math.max(1, Math.round(review.rating || 5))
                      )}.svg`}
                      className='h-20px'
                      alt={`${review.rating || 5} star rating`}
                    />
                    <span className='fw-semibold fs-7 text-nowrap ms-3'>{formatReviewDate(review.date)}</span>
                  </div>

                  <p className='lh-sm fs-7 mb-0'>{review.text}</p>

                  {isClickable && (
                    <a
                      href={review.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='fs-8 text-primary fw-semibold mt-3 text-decoration-none'
                      onClick={(event) => event.stopPropagation()}
                    >
                      {getReviewSourceLabel(review.integration)}
                      <i className='bi bi-box-arrow-up-right ms-1 fs-9' />
                    </a>
                  )}
                </div>
              </Carousel.Item>
            )
          })}
        </Carousel>

        {showControls && (
          <div className='d-flex justify-content-center align-items-center gap-2 mt-3'>
            {reviews.map((review, index) => (
              <button
                key={review.id}
                type='button'
                className='border-0 rounded-pill p-0 bg-white'
                style={{
                  width: index === activeIndex ? '24px' : '8px',
                  height: '4px',
                  opacity: index === activeIndex ? 1 : 0.45,
                  transition: 'width 0.2s ease, opacity 0.2s ease',
                }}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to review ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SenjaReviewsCarousel
